import type { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { Check, Minus } from "lucide-react"

import { StatusBadge } from "@/components/ui/status-badge"
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercent,
} from "@/lib/formatters"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import type { Field } from "./types"

/**
 * One em dash, muted, for every absent value in the product. `formatDateTime`
 * already returns this; everything else routes through `renderField`, so a
 * blank cell reads the same whether the missing thing was a date, a total or a
 * name.
 */
export const EMPTY_MARK = "—"

export type Alignment = "left" | "right"

/**
 * Right for anything a reader compares by scanning down the column — figures
 * and dates, whose digits then line up. Left for everything else. The header
 * takes the same alignment, because a right-aligned total under a left-aligned
 * label reads as a mistake.
 */
const RIGHT_ALIGNED: ReadonlySet<string> = new Set([
  "number",
  "currency",
  "percent",
  "date",
  "datetime",
  "actions",
])

export function fieldAlignment<TData>(field?: Field<TData>): Alignment {
  return field && RIGHT_ALIGNED.has(field.type) ? "right" : "left"
}

export function alignmentClass(alignment: Alignment) {
  return alignment === "right" ? "text-right" : "text-left"
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === ""
}

function Empty() {
  return <span className="text-muted-foreground">{EMPTY_MARK}</span>
}

function Truncated({ text, className }: { text: string; className?: string }) {
  return (
    <span className={cn("block max-w-[24ch] truncate", className)} title={text}>
      {text}
    </span>
  )
}

type RenderOptions = {
  /** The record's zone, never the browser's. See .claude/rules/react.md §16. */
  timeZone?: string
}

/**
 * Turns a primitive from the row data into the cell the design system asks for.
 * Every decision about weight, colour and alignment is made here once, so a
 * currency column cannot be semibold on one screen and regular on the next.
 */
export function renderField<TData>(
  field: Field<TData>,
  value: unknown,
  row: TData,
  options: RenderOptions = {}
): ReactNode {
  switch (field.type) {
    case "title": {
      if (isBlank(value)) return <Empty />
      const text = String(value)
      // 500, never 600 or 700. DESIGN.md spends 600 on figures and reserves 700
      // for marketing, and there are only two weights to go around: if the name
      // and the total are both 600, neither of them wins.
      const weight = cn("font-medium", field.mono && "font-mono text-xs")

      if (field.href) {
        return (
          <Link href={field.href(row)} className={cn(weight, "hover:underline")}>
            {field.truncate ? <Truncated text={text} /> : text}
          </Link>
        )
      }

      return field.truncate ? (
        <Truncated text={text} className={weight} />
      ) : (
        <span className={weight}>{text}</span>
      )
    }

    case "text": {
      if (isBlank(value)) return <Empty />
      const text = String(value)

      return field.truncate ? <Truncated text={text} /> : <span>{text}</span>
    }

    case "number":
      if (isBlank(value)) return <Empty />

      return (
        <span className="tabular-nums">
          {formatNumber(value as number | string, { decimals: field.decimals })}
        </span>
      )

    // Figures are the one place `font-semibold` is spent. See DESIGN.md, the
    // Dot Rule.
    case "currency":
      if (isBlank(value)) return <Empty />

      return (
        <span className="text-foreground font-semibold tabular-nums">
          {formatMoney(value as number | string, { currency: field.currency })}
        </span>
      )

    case "percent":
      if (isBlank(value)) return <Empty />

      return (
        <span className="tabular-nums">
          {formatPercent(value as number | string, { decimals: field.decimals })}
        </span>
      )

    case "date":
      if (isBlank(value)) return <Empty />

      return (
        <span className="text-muted-foreground tabular-nums">
          {formatDate(value as string, { timeZone: field.timeZone ?? options.timeZone })}
        </span>
      )

    case "datetime":
      if (isBlank(value)) return <Empty />

      return (
        <span className="text-muted-foreground tabular-nums">
          {formatDateTime(value as string, { timeZone: field.timeZone ?? options.timeZone })}
        </span>
      )

    // The glyph carries the state and the sr-only label carries the meaning, so
    // the column is not a row of shapes to a screen reader.
    case "boolean": {
      if (isBlank(value)) return <Empty />
      const on = Boolean(value)

      return (
        <span className="inline-flex items-center">
          {on ? (
            <Check className="text-success size-4" aria-hidden="true" />
          ) : (
            <Minus className="text-muted-foreground size-4" aria-hidden="true" />
          )}
          <span className="sr-only">
            {on
              ? t("ui.react.components.ui.data_table.boolean_true")
              : t("ui.react.components.ui.data_table.boolean_false")}
          </span>
        </span>
      )
    }

    case "status": {
      if (isBlank(value)) return <Empty />
      const stored = String(value)
      const label = field.labelKey
        ? String((row as Record<string, unknown>)[field.labelKey] ?? stored)
        : stored

      return <StatusBadge tone={field.tones[stored] ?? "disabled"} label={label} />
    }

    case "code":
      if (isBlank(value)) return <Empty />

      return <span className="text-muted-foreground font-mono text-xs">{String(value)}</span>

    case "link": {
      if (isBlank(value)) return <Empty />

      return (
        <Link href={field.href(row)} className="hover:underline">
          {String(value)}
        </Link>
      )
    }

    case "actions":
      return <div className="flex justify-end gap-2">{field.render(row)}</div>
  }
}
